import { AlertCircle } from "lucide-react";

/**
 * Shared field primitives for the multi-step inquiry flow.
 *
 * Kept intentionally lean — no framer-motion here so steps stay snappy.
 * Style tokens follow the site-wide `.pg-input`, gold/teal accent system.
 */

export const FieldLabel = ({ htmlFor, required, children, hint }) => (
    <div className="flex flex-col gap-1">
        <label
            htmlFor={htmlFor}
            className="text-[11px] font-semibold tracking-normal"
            style={{ color: "var(--pg-fg)" }}
        >
            {children}{required && <span style={{ color: "var(--pg-accent)" }}> *</span>}
        </label>
        {hint && (
            <span className="text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>{hint}</span>
        )}
    </div>
);

export const FieldError = ({ error }) =>
    error ? (
        <p className="flex items-center gap-1.5 text-[11px]" style={{ color: "#E5736F" }}>
            <AlertCircle className="h-3 w-3" /> {error}
        </p>
    ) : null;

export const TextField = ({
    id, label, value, onChange, required, error, placeholder = " ", type = "text", hint, testId, autoComplete,
}) => (
    <div className="flex flex-col gap-1.5">
        <FieldLabel htmlFor={id} required={required} hint={hint}>{label}</FieldLabel>
        <input
            id={id}
            type={type}
            value={value ?? ""}
            onChange={onChange}
            placeholder={placeholder}
            data-testid={testId}
            autoComplete={autoComplete}
            aria-invalid={error ? "true" : undefined}
            className={`pg-input${error ? " pg-input--error" : ""}`}
        />
        <FieldError error={error} />
    </div>
);

export const TextareaField = ({
    id, label, value, onChange, required, error, placeholder = " ", rows = 5, hint, testId,
}) => (
    <div className="flex flex-col gap-1.5">
        <FieldLabel htmlFor={id} required={required} hint={hint}>{label}</FieldLabel>
        <textarea
            id={id}
            value={value ?? ""}
            onChange={onChange}
            placeholder={placeholder}
            rows={rows}
            data-testid={testId}
            aria-invalid={error ? "true" : undefined}
            className={`pg-input${error ? " pg-input--error" : ""}`}
            style={{ resize: "vertical", minHeight: 120 }}
        />
        <FieldError error={error} />
    </div>
);

export const SectionHead = ({ eyebrow, title, description }) => (
    <div className="mb-8">
        {eyebrow && (
            <span className="pg-step-eyebrow" data-testid="step-eyebrow">
                {eyebrow}
            </span>
        )}
        <h2
            className="pg-reveal-head font-display mt-4 text-[clamp(1.85rem,3.6vw,2.75rem)] leading-[1.05]"
            style={{ color: "var(--pg-fg)" }}
            data-testid="step-title"
        >
            {title}
        </h2>
        {description && (
            <p className="mt-4 max-w-xl text-sm sm:text-base leading-relaxed" style={{ color: "var(--pg-fg-muted)" }} data-testid="step-description">
                {description}
            </p>
        )}
    </div>
);
